/**
 * @module support/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */

import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { APIError } from '@support/api/common/error.interface';
import { EndpointConfigInterface } from '@core/interfaces/endpoint-configuration.interface';
import { ORDER_SUBSTATUS_CODE } from '@experience/error-pages/constants/error.constants';
/** */

@Injectable()
export class GlobalErrorService {
  /**
   * global error subject
   */
  public globalErrorSubject: Subject<APIError[]>;
  /**
   * global error observable
   */
  public globalErrorObservable: Observable<APIError[]>;
  /**
   * global error subject
   */
  public checkDuplicateErrorSubject: Subject<EndpointConfigInterface>;
  /**
   * global error observable
   */
  public checkDuplicateErrorObservable: Observable<EndpointConfigInterface>;
  /**
   * custom error subject
   */
  public customErrorSubject: Subject<APIError[]>;
  /**
   * custom error observable
   */
  public customErrorObservable: Observable<APIError[]>;
  /**
   * custom error subject
   */
  public clearGlobalErrorSubject: Subject<void>;
  /**
   * custom error observable
   */
  public clearGlobalErrorObservable: Observable<void>;

  /**
   * custom spinner subject
   */
  public clearSpinnerSubject: Subject<string>;

  /**
   * custom spinner observable
   */
  public clearSpinnerObservable: Observable<string>;

  /**
   * emitter to hide the error
   */
  public hideErrorEmitter: Subject<string>;
  /**
   * emitter to get the error
   */
  public errorEmitter: Subject<APIError[]>;
  constructor() {
    this.globalErrorSubject = new Subject();
    this.globalErrorObservable = this.globalErrorSubject.asObservable();
    this.checkDuplicateErrorSubject = new Subject();
    this.checkDuplicateErrorObservable = this.checkDuplicateErrorSubject.asObservable();
    this.customErrorSubject = new Subject();
    this.customErrorObservable = this.customErrorSubject.asObservable();
    this.clearGlobalErrorSubject = new Subject();
    this.clearGlobalErrorObservable = this.clearGlobalErrorSubject.asObservable();
    this.clearSpinnerSubject = new Subject();
    this.clearSpinnerObservable = this.clearSpinnerSubject.asObservable();
    this.hideErrorEmitter = new Subject();
    this.errorEmitter = new Subject();
  }
  /**
   * method to set global error message
   * @returns void
   */
  public setGlobalErrorMessage(apiError: APIError[]): void {
    this.globalErrorSubject.next(apiError);
  }

  /**
   * method to check duplicate global error message
   * @returns void
   */
  public checkDuplicateGlobalErrorMessage(endpointConfig: EndpointConfigInterface): void {
    this.checkDuplicateErrorSubject.next(endpointConfig);
  }

  /**
   * method to check and update user error message if it includes pipe
   * @returns void
   */
  public checkUserErrorMessage(customError: APIError[]): APIError[] {
    customError.filter((error: APIError) => {
      if (error && error.userMessage && error.userMessage.includes('|')) {
        error.userMessage = error.userMessage.replace(/\|/g, '<br/>');
      }
    });
    return customError;
  }

  /**
   * method to check error sub status code
   * @returns void
   */
  public checkErrorSubStatusCode(customError: APIError[]): boolean {
    for (let i = 0; i < customError.length; i++) {
      if (customError[i].subStatusCode === ORDER_SUBSTATUS_CODE.ORDER_ERROR) {
        return true;
      }
    }
    return false;
  }

  /**
   * method to clear global error message
   * @returns void
   */
  public clearGlobalErrorMessage(): void {
    this.clearGlobalErrorSubject.next();
  }

  /**
   * method to clear spinner
   * @returns void
   */
  public clearSpinner(route: string): void {
    this.clearSpinnerSubject.next(route);
  }
  /**
   * method to set error message on stack for error message component
   * @returns void
   */
  public setError(errorData: APIError[]): void {
    this.errorEmitter.next(errorData);
  }
  /**
   * method to remove error message from stack.
   * @returns void
   */
  public clearError(key?: string): void {
    this.hideErrorEmitter.next(key);
  }
}
